// Admin Service - GraphQL operations for Admin role
import { graphqlClient } from '../graphqlClient';

// ==================== Engine Operations ====================

export const getAllEngines = async () => {
  const query = `
    query GetAllEngines {
      getAllEngines {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllEngines;
};

export const getEngineById = async (id) => {
  const query = `
    query GetEngineById($id: String!) {
      getEngineById(id: $id) {
        id
        code
        email
        name
        status
        depositFee
        withdrawalFee
        commissionRate
        depositCommission
        withdrawalCommission
        percentageFee
        allowedProviders
        maxResellers
        maxMerchants
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getEngineById;
};

export const getMyCreatedEngines = async () => {
  const query = `
    query GetMyCreatedEngines {
      getMyCreatedEngines {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyCreatedEngines;
};

export const createEngine = async (input) => {
  const mutation = `
    mutation CreateEngine($input: CreateEngineInput!) {
      createEngine(input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createEngine;
};

export const updateEngine = async (id, input) => {
  const mutation = `
    mutation UpdateEngine($id: String!, $input: UpdateEngineInput!) {
      updateEngine(id: $id, input: $input) {
        id
        code
        email
        name
        status
        depositFee
        withdrawalFee
        commissionRate
        depositCommission
        withdrawalCommission
        percentageFee
        allowedProviders
        maxResellers
        maxMerchants
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateEngine;
};

export const deleteEngine = async (id) => {
  const mutation = `
    mutation DeleteEngine($id: String!) {
      deleteEngine(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteEngine;
};

export const changeEnginePassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeEnginePassword($input: ChangePasswordInput!) {
      changeEnginePassword(input: $input)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input: { id, newPassword } });
  return result.changeEnginePassword;
};

// ==================== Reseller Operations ====================

export const getAllResellers = async () => {
  const query = `
    query GetAllResellers {
      getAllResellers {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllResellers;
};

export const getResellerById = async (id) => {
  const query = `
    query GetResellerById($id: String!) {
      getResellerById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        depositFee
        withdrawalFee
        commissionRate
        contactName
        contactPhone
        supportEmail
        notes
        maxMerchants
        allowedProviders
        routingOverride
        apiKey
        secretKey
        parentName
        parentType
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getResellerById;
};

export const getMyCreatedResellers = async () => {
  const query = `
    query GetMyCreatedResellers {
      getMyCreatedResellers {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyCreatedResellers;
};

export const createReseller = async (input) => {
  const mutation = `
    mutation CreateReseller($input: CreateResellerInput!) {
      createReseller(input: $input) {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createReseller;
};

export const updateReseller = async (id, input) => {
  const mutation = `
    mutation UpdateReseller($id: String!, $input: UpdateResellerInput!) {
      updateReseller(id: $id, input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateReseller;
};

export const deleteReseller = async (id) => {
  const mutation = `
    mutation DeleteReseller($id: String!) {
      deleteReseller(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteReseller;
};

export const changeResellerPassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeResellerPassword($input: ChangePasswordInput!) {
      changeResellerPassword(input: $input)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input: { id, newPassword } });
  return result.changeResellerPassword;
};

// ==================== Merchant Operations ====================

export const getAllMerchants = async () => {
  const query = `
    query GetAllMerchants {
      getAllMerchants {
        id
        code
        email
        name
        status
        callbackUrl
        webhookUrl
        depositEnabled
        withdrawalEnabled
        notes
        ipWhitelist
        rateLimit
        minDeposit
        maxDeposit
        minWithdrawal
        maxWithdrawal
        dailyDepositLimit
        percentageFee
        dailyWithdrawalLimit
        depositFee
        withdrawalFee
        feeType
        fixedFee
        settlementPeriod
        balance
        contactName
        contactEmail
        contactPhone
        apiKey
        secretKey
        createdByAdminId
        createdByEngineId
        createdByResellerId
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllMerchants || [];
};

// ==================== Merchant Operations ====================

export const getResellerMerchants = async (resellerId) => {
  const query = `
    query GetResellerMerchants($resellerId: String!) {
      getResellerMerchants(resellerId: $resellerId) {
        id
        code
        email
        name
        status
        callbackUrl
        webhookUrl
        depositEnabled
        withdrawalEnabled
        notes
        ipWhitelist
        rateLimit
        minDeposit
        maxDeposit
        minWithdrawal
        maxWithdrawal
        dailyDepositLimit
        dailyWithdrawalLimit
        depositFee
        withdrawalFee
        feeType
        fixedFee
        percentageFee
        settlementPeriod
        balance
        contactName
        contactEmail
        contactPhone
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { resellerId });
  return result.getResellerMerchants || [];
};

export const getMerchantWithEngineId = async (engineId, page = 1, limit = 10, search = "") => {
  const query = `
    query GetMerchantWithEngineId($engineId: String!, $page: Int!, $limit: Int!, $search: String) {
      getMerchantWithEngineId(engineId: $engineId, page: $page, limit: $limit, search: $search) {
        data {
          id
          code
          email
          name
          status
          feeType
          percentageFee
          fixedFee
          depositFee
          withdrawalFee
          createdAt
          updatedAt
        }
        total
        totalPages
        page
        limit
        totalActiveMerchants
        totalRegisteredMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query, { engineId, page, limit, search });
  return result.getMerchantWithEngineId;
};

// ==================== Withdrawals (By MerchantId) ====================

export const getMerchantWithdrawalsById = async (merchantId) => {
  const query = `
    query MerchantWithdrawalsById($merchantId: String!) {
      merchantWithdrawalsById(merchantId: $merchantId) {
        id
        merchant_id
        amount
        bank_code
        bank_account
        account_name
        bank_name
        network
        wallet_address
        status
        createdAt
        updatedAt
        merchants {
          id
          name
          email
        }
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantId });
  return result.merchantWithdrawalsById || [];
};

// ==================== Dashboard live stats========================

export const getDashboardLiveMetrics = async () => {
  const query = `
    query DashboardLiveMetrics {
      dashboardLiveMetrics {
        totalDepositsAmount
        totalWithdrawalsAmount
        successRate

        adminBalance
        adminBalanceChange

        depositChange
        withdrawalChange
        successRateChange

        totalActiveMerchants
        activeMerchantChange
      }
    }
  `;

  const result = await graphqlClient.query(query);

  return result?.dashboardLiveMetrics ?? {
    totalDepositsAmount: 0,
    totalWithdrawalsAmount: 0,
    successRate: 0,
    depositChange: 0,
    withdrawalChange: 0,
    successRateChange: 0,
    totalActiveMerchants: 0,
    activeMerchantChange: 0,
  };
};

// ==================== Merchant live chart ========================

export const getMerchantChart = async ({ startDate, endDate, merchantIds }) => {
  const query = `
    query TransactionChart($startDate: String!, $endDate: String!, $merchantIds: [String!]) {
      transactionChart(startDate: $startDate, endDate: $endDate, merchantIds: $merchantIds) {
        name
        deposits
        withdrawals
      }
    }
  `;

  const result = await graphqlClient.query(query, {
    startDate,
    endDate,
    merchantIds: merchantIds || [],
  });

  return result?.transactionChart ?? [];
};

// ==================== Merchant Dashboard live stats ========================

export const getMerchantDashboardLiveMetrics = async (merchantId) => {
  const query = `
    query MerchantDashboardLiveMetrics($merchantId: String!) {
      merchantDashboardLiveMetrics(merchantId: $merchantId) {
        totalWithdrawals
        totalDeposits
        totalVolume
        todayVolume
        netBalance
        feeBalance
        successRate
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantId });

  return result?.merchantDashboardLiveMetrics ?? {
    totalWithdrawals: 0,
    totalDeposits: 0,
    totalVolume: 0,
    todayVolume: 0,
    successRate: 0,
  };
};

// ==================== Admin Engine Dashboard live stats========================

export const getEngineDashboardLiveMetrics = async (engineId) => {
  const query = `
    query EngineDashboardLiveMetrics($engineId: String!) {
      engineDashboardLiveMetrics(engineId: $engineId) {
        totalVolume
        todayVolume
        totalTransactions
        successRate
        resellers
        merchants
      }
    }
  `;

  const result = await graphqlClient.query(query, { engineId });

  return result?.engineDashboardLiveMetrics ?? {
    totalVolume: 0,
    todayVolume: 0,
    totalTransactions: 0,
    successRate: 0,
    resellers: 0,
    merchants: 0,
  };
};

// ==================== Admin Reseller Dashboard live stats========================

export const getResellerDashboardLiveMetrics = async (resellerId) => {
  const query = `
    query ResellerDashboardLiveMetrics($resellerId: String!) {
      resellerDashboardLiveMetrics(resellerId: $resellerId) {
        totalVolume
        todayVolume
        totalTransactions
        successRate
        merchants
      }
    }
  `;

  const result = await graphqlClient.query(query, { resellerId });

  return result?.resellerDashboardLiveMetrics ?? {
    totalVolume: 0,
    todayVolume: 0,
    totalTransactions: 0,
    successRate: 0,
    merchants: 0,
  };
};




//=================== Get merchant by id =======================

export const getMerchantById = async (id) => {
  const query = `
    query GetMerchantById($id: String!) {
      getMerchantById(id: $id) {
        id
        code
        email
        name
        status
        callbackUrl
        webhookUrl
        depositEnabled
        withdrawalEnabled
        notes
        ipWhitelist
        rateLimit
        minDeposit
        maxDeposit
        minWithdrawal
        maxWithdrawal
        dailyDepositLimit
        percentageFee
        dailyWithdrawalLimit
        feeType
        fixedFee
        settlementPeriod
        balance
        contactName
        contactEmail
        contactPhone
        apiKey
        secretKey
        createdByAdminId
        createdByEngineId
        createdByResellerId
        depositFee
        withdrawalFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getMerchantById;
};

export const getMyCreatedMerchants = async () => {
  const query = `
    query GetMyCreatedMerchants {
      getMyCreatedMerchants {
        id
        code
        email
        name
        status
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyCreatedMerchants;
};

export const createMerchant = async (input) => {
  const mutation = `
    mutation AddMerchant($data: AddMerchantInput!) {
      addMerchant(data: $data) {
        id
        code
        email
        name
        status
        apiKey
        secretKey
        createdAt
        updatedAt
        createdByAdminId
        createdByEngineId
        createdByResellerId
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { data: input });
  return result.addMerchant;
};

export const updateMerchant = async (id, input) => {
  const mutation = `
    mutation UpdateMerchant($id: String!, $input: UpdateMerchantInput!) {
      updateMerchant(id: $id, input: $input) {
        id
        code
        email
        name
        status
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateMerchant;
};

export const deleteMerchant = async (id) => {
  const mutation = `
    mutation DeleteMerchant($id: String!) {
      deleteMerchant(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteMerchant;
};

export const changeMerchantPassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeMerchantPassword($input: ChangePasswordInput!) {
      changeMerchantPassword(input: $input)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input: { id, newPassword } });
  return result.changeMerchantPassword;
};

// ==================== Dashboard Stats ====================

export const getAdminDashboardStats = async () => {
  const query = `
    query GetAdminDashboardStats {
      getAdminDashboardStats {
        totalEngines
        totalResellers
        totalMerchants
        myCreatedEngines
        myCreatedResellers
        myCreatedMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAdminDashboardStats;
};

export const getGlobalDashboardStats = async () => {
  const query = `
    query GetGlobalDashboardStats {
      getGlobalDashboardStats {
        totalEngines
        totalResellers
        totalMerchants
        activeEngines
        activeResellers
        activeMerchants
        inactiveEngines
        inactiveResellers
        inactiveMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getGlobalDashboardStats;
};

// ==================== Admin Operations ====================

export const getAllAdmins = async () => {
  const query = `
    query GetAllAdmins {
      getAllAdmins {
        id
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllAdmins;
};

export const createAdmin = async (input) => {
  const mutation = `
    mutation CreateAdmin($input: CreateAdminInput!) {
      createAdmin(input: $input) {
        id
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createAdmin;
};

// ==================== Staff Operations ====================

export const getMyStaffUsers = async () => {
  const query = `
    query GetMyStaffUsers {
      getMyStaffUsers {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
          panelType
          permissions
          isSystem
        }
        permissions
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyStaffUsers;
};

export const getStaffUserById = async (id) => {
  const query = `
    query GetStaffUserById($id: String!) {
      getStaffUserById(id: $id) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
          panelType
          permissions
          isSystem
        }
        permissions
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getStaffUserById;
};

export const getAvailableRoles = async () => {
  const query = `
    query GetAvailableRoles {
      getAvailableRoles {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAvailableRoles;
};

export const createStaffUser = async (input) => {
  const mutation = `
    mutation CreateStaffUser($input: CreateStaffUserInput!) {
      createStaffUser(input: $input) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
        }
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createStaffUser;
};

export const updateStaffUser = async (id, input) => {
  const mutation = `
    mutation UpdateStaffUser($id: String!, $input: UpdateStaffUserInput!) {
      updateStaffUser(id: $id, input: $input) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
        }
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateStaffUser;
};

export const deleteStaffUser = async (id) => {
  const mutation = `
    mutation DeleteStaffUser($id: String!) {
      deleteStaffUser(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteStaffUser;
};

export const assignRoleToStaff = async (input) => {
  const mutation = `
    mutation AssignRoleToStaff($input: AssignRoleInput!) {
      assignRoleToStaff(input: $input) {
        id
        email
        name
        roles {
          id
          name
          description
        }
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.assignRoleToStaff;
};

export const removeRoleFromStaff = async (input) => {
  const mutation = `
    mutation RemoveRoleFromStaff($input: AssignRoleInput!) {
      removeRoleFromStaff(input: $input) {
        id
        email
        name
        roles {
          id
          name
          description
        }
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.removeRoleFromStaff;
};

export const createStaffRole = async (input) => {
  const mutation = `
    mutation CreateStaffRole($input: CreateStaffRoleInput!) {
      createStaffRole(input: $input) {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createStaffRole;
};

export const deleteStaffRole = async (id) => {
  const mutation = `
    mutation DeleteStaffRole($id: String!) {
      deleteStaffRole(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteStaffRole;
};

export const updateStaffRole = async (id, input) => {
  const mutation = `
    mutation UpdateStaffRole($id: String!, $input: UpdateStaffRoleInput!) {
      updateStaffRole(id: $id, input: $input) {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateStaffRole;
};

// ==================== Webhook Logs ====================

export const getWebhookLogs = async (limit) => {
  const query = `
    query GetWebhookLogs($limit: Int) {
      getWebhookLogs(limit: $limit) {
        id
        qrCodeId
        paymentId
        merchantId
        merchantName
        event
        url
        httpStatus
        responseBody
        errorMessage
        requestPayload
        createdAt
      }
    }
  `;
  const result = await graphqlClient.query(query, limit != null ? { limit } : {});
  return result.getWebhookLogs || [];
};


export const getWithdrawalsPagination = async (page, limit, search, filters, merchants) => {
  const query = `
    query MerchantWithdrawalsPagination($page: Int, $limit: Int, $search: String, $filters: WithdrawalFilterInput, $merchants: [String!]) {
      merchantWithdrawalsPagination(page: $page, limit: $limit, search: $search, filters: $filters, merchants: $merchants) {
        data {
          id
          merchant_id
          amount
          net_amount
          total_fee
          bank_code
          bank_account
          account_name
          bank_name
          network
          wallet_address
          status
          createdAt
          updatedAt
          merchants {
            id
            name
            email
          }
        }
        total
        totalPages
        page
        limit
        totalAmount
        totalSuccessAmount
        totalPendingAmount
        totalFailedAmount
        successCount
        pendingCount
        failedCount
      }
    }
  `;
  const result = await graphqlClient.query(query, { page, limit, search, filters, merchants });
  return result.merchantWithdrawalsPagination;
};